import { HttpStatusCode } from "../HttpClient/IHttpClient";
import { ICombineByTransfers } from "../../Engine/src/Engine/Core/ModalManager/Modals/UploadSection/Types";
import { AbsoluteModelPath } from "../ModelPath/ModelPath";
import { AbsoluteModelFieldPath } from "../ModelPath/AbsoluteModelFieldPath";
import { FormSettings } from "../../Engine/src/Forms/Types/FormSettings";
import { AUOOptions } from "../../Engine/src/Forms/Types/AUOOptions";
import { WorkspaceResourceType } from "../../Engine/src/ContractorsServiceIntegration/Types/WorkspaceTypes";

import { ServerErrorLog } from "./Types/ErrorLog/ServerErrorLog";
import { ServerInnerResponse, ServerJsonResponse } from "./Types/GetInner/ServerInnerResponse";
import {
    ExcelsToPartialFufsResponse,
    FileProperties,
    ServerErrorResponse,
    UploadFilePropertiesResponse,
} from "./Types/DraftActions/FileProperties";
import { CreateDraftResponse } from "./Types/CreateDraft/CreateDraftResponse";
import { CreateDraftFromDriveParams, CreateEmptyDraftParams } from "./Types/CreateDraft/CreateDraftParams";
import { CreateExplanatoryNoteResponse } from "./Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import { DraftFullId } from "./Types/DraftFullId";
import { ServerChangeSet } from "./Types/ChangeSet/ServerChangeSet";
import { ServerTask, VerifyAndFixFnsRequirementsTaskInfo } from "./Types/Tasks/Tasks";
import { GetByInnResponse } from "./Types/Focus/FocusTypes";
import { AttachInfo } from "./Types/Attachments/Attacments";
import { DraftPresentationType } from "./Types/Presentations/DraftPresentationType";
import { PresentationContent } from "./Types/Presentations/PresentationContent";
import { ConvertDraftRequest } from "./Types/DraftActions/ConvertDraftRequest";
import { ServerRequisitesResponse } from "./Types/GetInner/ServerRequisitesResponse";
import {
    CombinatorType,
    CombineFufResult,
    FufCombineMethod,
    ImportArguments,
} from "./Types/DraftActions/CombineFufResult";
import { FeatureFlagsResponse } from "./Types/FeatureFlags/FeatureFlagsResponse";
import { ImportEmployeesRequest } from "./Types/Staff/ImportEmployeesRequest";
import { MassActionParameters } from "./Types/MassActions/MassActionParameters";
import { PrintType } from "./Types/Print/PrintType";
import { PrintResponse } from "./Types/Print/PrintResponse";
import { ServerRawResponse } from "./Types/GetInner/ServerRawResponse";
import { CandyPopdProtocol } from "./Types/ErrorLog/CandyPopdProtocol";
import { InstancesDeletingRule } from "./Types/InstancesDeleting/InstancesDeletingRule";
import { ValueForInstancesSettingRule } from "./Types/MassActions/ValueForInstancesSettingRule";
import { GetAttachmentsResponse } from "./Types/Attachments/GetAttachmentsResponse";
import { InnerQueryRequest } from "./Types/QueryCollecton/InnerQueryRequest";
import { InnerQueryResponse } from "./Types/QueryCollecton/InnerQueryResponse";
import { ResolveInnerPathRequest } from "./Types/ErrorLog/ResolveInnerPathRequest";
import { ResolvePathResponse } from "./Types/ErrorLog/ResolvePathResponse";
import { InnerHistoryResponse, InnerHistoryUndoResponseWithStatus } from "./Types/HistoryEditing/InnerHistoryResponse";
import { PollingTaskWithResult } from "./Types/PollingTasks/PollingTasks";

export interface ICandyApiClient {
    forDraft(draftFullId: DraftFullId): ICandyApiDraftClient;

    // region Create draft
    createDraft(ns: string, gfv: string): Promise<CreateDraftResponse>;
    createDraft(ns: string, gfv: undefined, body: Buffer): Promise<CreateDraftResponse>;
    createDraftFromDrive(params: CreateDraftFromDriveParams): Promise<CreateDraftResponse>;
    createEmptyDraft(params: CreateEmptyDraftParams): Promise<CreateDraftResponse>;
    // endregion

    //region Focus
    getInfoByInns(draftFullId: DraftFullId, inns: readonly string[]): Promise<GetByInnResponse>;
    //endregion

    // region Diagnostics
    getAllGfvs(abortSignal?: AbortSignal): Promise<string[]>;
    forceGetAllGfvs(): Promise<string[]>;
    // endregion

    checkAlive(): Promise<void>;

    getFeatureFlags(abonId?: string, orgId?: string, userId?: string): Promise<FeatureFlagsResponse>;

    // region DirectoryId
    bindExternOrganization(orgId: string, resourceType: WorkspaceResourceType): Promise<void>;

    getWorkspaceDirectoryId(options: {
        orgId?: string;
        resourceType: WorkspaceResourceType;
        inn: string;
        kpp?: string;
        parentResourceType?: string;
        organizatorProductName?: "poa" | "poa-staging";
    }): Promise<string>;
    // endregion

    convertToPdfAndGetMeta(driveUri: string): Promise<ConvertedPDFMetaResponse>;
    convertToPdf(driveUri: string): Promise<ConvertedPdfResponse>;
}

export interface ICandyApiDraftTasksClient {
    getAllTasks(): Promise<ServerTask[]>;
    getTaskById(taskId: string): Promise<ServerTask | undefined>;
    enqueueTask(taskId: string, taskInfo: VerifyAndFixFnsRequirementsTaskInfo): Promise<void>;
    cancelTask(taskId: string): Promise<void>;
}

export interface ICandyApiDraftAttachmentsClient {
    getAttachmentById(attachmentId: string): Promise<AttachInfo>;
    getAllAttachments(): Promise<AttachInfo[]>;
    createOrUpdateAttachments(attachments: readonly AttachInfo[]): Promise<void>;
    createDraftAttachment(gfv: string, path: string): Promise<AttachInfo>;
    openLoadedAttachmentAsDraft(id: string): Promise<AttachInfo>;
    removeAttachments(attachmentIds: readonly string[]): Promise<void>;
}

export interface PathLoadSpecification {
    pathOrPathPrefix: AbsoluteModelPath | AbsoluteModelFieldPath | "*";
    skip: boolean;
}

export type CheckScenarioType = "readonly";
export interface IInnerChangeSuccessResult {
    dataVersion: number;
    changedPaths?: string[];
}
export type IInnerChangeResult = IInnerChangeSuccessResult | HttpStatusCode;

export interface DraftSettings {
    isReadOnly?: FormSettings["isReadOnly"];
    isIndividual?: FormSettings["isIndividual"];
    isForeign?: FormSettings["isForeign"];
    employerId?: FormSettings["employerId"];
}

export type NormalizerNames = "formulas" | "formulasWithInitializers";

export interface NormalizationProgressTask {
    id: string;
    status: "InProgress" | "Success" | "Error";
    progress: number;
}

export interface RequestNdsFileOptions {
    driveUri: string;
    ignoreSizeCheck?: boolean;
    declarationGfv: string;
    requestedGfv?: string;
}

export interface ICandyApiDraftClient extends ICandyApiDraftTasksClient, ICandyApiDraftAttachmentsClient {
    // region Explanatory note
    getExplanatoryNote(): Promise<CreateExplanatoryNoteResponse>;
    // endregion

    // region Error log
    getErrorLog(): Promise<ServerErrorLog>;
    checkByPopd(): Promise<CandyPopdProtocol>;
    getErrorLogWithCheckScenarios(checkScenarios: CheckScenarioType[]): Promise<ServerErrorLog>;
    getExpertErrorLog(auo: Partial<AUOOptions>): Promise<ServerErrorLog>;
    getHrefToErrorLogPdf(errorLogName: string, propsName: string): string;
    // endregion

    // region Get draft requisites
    getRequisites(requisiteList: readonly string[]): Promise<ServerRequisitesResponse>;
    // endregion

    //region Get draft inner
    getInner(paths: undefined | readonly string[]): Promise<ServerInnerResponse>;
    getPartialInner(rules: readonly PathLoadSpecification[], maxInstanceCount?: number): Promise<ServerInnerResponse>;
    getPartialInnerAsJsonTree(
        rules: readonly PathLoadSpecification[],
        maxInstanceCount?: number,
        skipNormalization?: boolean
    ): Promise<ServerJsonResponse>;
    getRawPartialInnerAsJsonTree(
        rules: readonly PathLoadSpecification[],
        maxInstanceCount?: number,
        skipNormalization?: boolean
    ): Promise<ServerRawResponse>;
    getDataVersion(): Promise<number>;
    startNormalizationTask(): Promise<NormalizationProgressTask>;
    getNormalizationTask(): Promise<NormalizationProgressTask>;
    //endregion

    // region Update draft inner
    sendChangeSet(
        data: ServerChangeSet,
        currentDataVersion: number,
        operationName?: string
    ): Promise<IInnerChangeResult>;
    sendChangeSetSync(data: ServerChangeSet, currentDataVersion: number): IInnerChangeResult;
    sendChangeWithNormalizationSet(
        data: ServerChangeSet,
        currentDataVersion: number,
        normalizerId?: NormalizerNames,
        operationName?: string
    ): Promise<IInnerChangeResult>;
    startPatchWithNormalizationTask(
        data: ServerChangeSet,
        currentDataVersion: number,
        normalizerId?: string,
        operationName?: string
    ): Promise<PollingTaskWithResult<IInnerChangeSuccessResult> | number>;
    getPatchWithNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeSuccessResult>>;
    // endregion

    // region Draft presentations
    setPresentation(type: DraftPresentationType, content: string | unknown): Promise<void>;
    patchPresentation(type: DraftPresentationType, content: PresentationContent): Promise<void>;
    // endregion

    // region Draft inner print
    print(type: PrintType, body?: string[]): Promise<PrintResponse>;
    // endregion

    // region Draft actions
    clearDraft(): Promise<void>;
    restoreToSourceFuf(): Promise<void>;
    convertDraftFromPrevGfv(request: ConvertDraftRequest): Promise<void>;
    getFileProperties(driveUri: string, ignoreSizeCheck?: boolean): Promise<FileProperties | undefined>;
    getFileNdsProperties(params: RequestNdsFileOptions): Promise<FileProperties[] | undefined>;
    prepareAndGetFileProperties(
        driveUri: string,
        gfv: string,
        ignoreSizeCheck?: boolean
    ): Promise<FileProperties | undefined>;
    uploadAndGetFileProperties(
        driveUri: string[],
        gfv: string,
        abortSignal?: AbortSignal
    ): Promise<UploadFilePropertiesResponse>;
    setFufByDriveUri(driveUri: string): Promise<void>;
    acceptMultipleTransferType(
        files: ICombineByTransfers[],
        abortSignal?: AbortSignal
    ): Promise<ServerErrorResponse | undefined>;
    excelsToPartialFufs(
        gfv: string,
        pathToTable: string,
        driveUris: string[]
    ): Promise<ExcelsToPartialFufsResponse | undefined>;
    importXmls(
        driveUris: string[],
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: string[]
    ): Promise<CombineFufResult | undefined>;
    setDraftSettings(settings: DraftSettings): Promise<void>;
    getDraftSettings(): Promise<DraftSettings>;
    // endregion

    normalizeByRules(rules: string): Promise<void>;

    getHrefToConvertationLog(): string;
    getHrefToDownloadInnerFuf(): string;
    getHrefToDownloadInner(): string;
    getHrefToDownloadClientInfo(): string;
    getHrefToDownloadFormsClientInfo(): string;
    getHrefToInnerEditor(): string;

    getFufWithAttachments(): Promise<FufWithAttachmentsResponse | undefined>;
    startGetFufWithAttachmentsTask(): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>>;
    getFufWithAttachmentsTask(
        format: "Json" | "DriveUri"
    ): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>>;
    startGetFufWithScenarioTask(scenario: string): Promise<PollingTaskWithResult<FufWithScenarioTaskResult>>;
    getFufWithScenarioTask(
        format: "Json" | "DriveUri",
        scenario: string
    ): Promise<PollingTaskWithResult<FufWithScenarioTaskResult>>;
    getPartialFuf(rules: string[]): Promise<GetPartialFufResponse | undefined>;
    getInnerFuf(): Promise<string | undefined>;
    startGetInnerFufTask(): Promise<PollingTaskWithResult<string>>;
    getInnerFufTask(): Promise<PollingTaskWithResult<string>>;

    downloadFuf(scenario?: string): Promise<void>;
    downloadInner(): Promise<void>;
    makeDownloadDriveLink(uri: string, fileName?: string): void;

    setMultipleWithNormalization(rules: ValueForInstancesSettingRule[]): Promise<IInnerChangeResult>;

    normalizeDirtyMasks(): Promise<IInnerChangeResult>;

    searchInstancesByText(
        text: string,
        pathPrefix: string,
        pathsToSearch: string[],
        maxInstanceCount?: number
    ): Promise<string[]>;

    queryCollection(request: InnerQueryRequest, abortSignal?: AbortSignal): Promise<InnerQueryResponse>;

    customNormalize(
        normalizerId: string | null,
        pathPrefixes: AbsoluteModelPath[],
        abortSignal?: AbortSignal
    ): Promise<IInnerChangeResult>;
    startCustomNormalizationTask(
        pathPrefixes: AbsoluteModelPath[],
        normalizerId?: string,
        operationName?: string
    ): Promise<PollingTaskWithResult<IInnerChangeResult>>;
    getCustomNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeResult>>;
    importEmployees(request: ImportEmployeesRequest): Promise<void>;
    runMassAction(actionId: string, parameters?: MassActionParameters): Promise<void>;
    deleteInstances(deletingRules: InstancesDeletingRule[]): Promise<void>;
    printExcel(pathToTable: string): Promise<PrintResponse | undefined>;

    startTransferTask(
        combineMethod: FufCombineMethod,
        combinatorType: CombinatorType,
        isImportByFuf: boolean,
        args?: ImportArguments
    ): Promise<PollingTaskWithResult<CombineFufResult>>;
    getTransferTask(taskId: string): Promise<PollingTaskWithResult<CombineFufResult>>;

    resolveInnerPath(request: ResolveInnerPathRequest, abortSignal?: AbortSignal): Promise<ResolvePathResponse>;
    getHistory(): Promise<InnerHistoryResponse | HttpStatusCode>;
    undoToChange(changeGUID: string): Promise<InnerHistoryUndoResponseWithStatus>;
    getGFV(): Promise<string>;
    getUser(): Promise<GetUserResponse>;
}

export interface GetUserResponse {
    Id: string | null;
    Ip: string | null;
    FirstName: string | null;
    MiddleName: string | null;
    LastName: string | null;
}

export interface GetPartialFufResponse {
    ContentUri: string;
    Name: string;
}

export interface BaseGetFufResponse {
    contentUri: string;
    name: string;
}

export interface FufWithAttachmentsResponse {
    fuf: BaseGetFufResponse;
    attachments: GetAttachmentsResponse[];
}

export type FufWithAttachmentsTaskResult = FufWithAttachmentsResponse | { resultUri: string };
export type FufWithScenarioTaskResult = FufWithAttachmentsResponse | { resultUri: string };

export interface CandyApiErrorResponse {
    message: string;
    errorCode: "InvalidEditSession" | string;
}

export interface ConvertedPDFMetaResponse {
    contentUri: string;
    isModified: boolean;
    pageCount: number;
    lastPageHeight: number;
    contentSizeBytes: number;
}

export interface ConvertedPdfResponse {
    contentUri: string;
    isModified: boolean;
    contentSizeBytes: number;
}
